import { useCallback, useEffect, useRef, useState } from 'react'

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

export interface UseAutosaveOptions<T> {
  value: T
  onSave: (value: T) => Promise<unknown>
  delayMs?: number
  maxWaitMs?: number
  enabled?: boolean
}

export interface AutosaveHandle {
  status: AutosaveStatus
  flush: () => Promise<void>
}

/**
 * 편집 중 값이 멎으면 지연 뒤 저장하고, 화면을 떠나거나 탭을 감출 때 마지막 상태를 남긴다.
 * 저장 실패는 상태로만 알리고 입력값은 그대로 둔다.
 *
 * 멎기를 기다리는 시계 하나로는 모자란다. 그 시계는 값이 바뀔 때마다 되감기므로 쉬지 않고
 * 쓰는 사람에게는 빈틈이 나지 않아 몇 분 동안 한 번도 저장이 돌지 않는다. 그래서 마지막
 * 저장에서 이만큼 지나면 입력이 이어지는 중에도 한 번 보내는 상한을 함께 둔다. 잃을 수 있는
 * 최대치가 이 값으로 묶인다.
 */
export function useAutosave<T>({
  value,
  onSave,
  delayMs = 800,
  maxWaitMs = Number.POSITIVE_INFINITY,
  enabled = true,
}: UseAutosaveOptions<T>): AutosaveHandle {
  const [status, setStatus] = useState<AutosaveStatus>('idle')
  const latest = useRef({ value, onSave, enabled })
  const savedSnapshot = useRef(JSON.stringify(value))
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** 지금 밀린 입력이 처음 생긴 시각. 상한을 재는 기준이라 저장이 서면 비운다. */
  const pendingSince = useRef<number | null>(null)

  useEffect(() => {
    latest.current = { value, onSave, enabled }
  })

  const flush = useCallback(async () => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const current = latest.current
    const snapshot = JSON.stringify(current.value)
    if (!current.enabled || snapshot === savedSnapshot.current) return

    setStatus('saving')
    try {
      await current.onSave(current.value)
      savedSnapshot.current = snapshot
      pendingSince.current = null
      setStatus('saved')
    } catch {
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    if (JSON.stringify(value) === savedSnapshot.current) {
      pendingSince.current = null
      return
    }

    const now = Date.now()
    pendingSince.current ??= now
    // 멎기를 기다리되 상한을 넘기지 않는다. 이미 넘겼으면 다음 틱에 곧바로 보낸다.
    const untilLimit = maxWaitMs - (now - pendingSince.current)
    const wait = Math.max(0, Math.min(delayMs, untilLimit))

    setStatus('pending')
    timer.current = setTimeout(() => void flush(), wait)
    return () => {
      if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [value, delayMs, maxWaitMs, enabled, flush])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void flush()
    }
    const handlePageHide = () => void flush()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      void flush()
    }
  }, [flush])

  return { status, flush }
}
